import type { BlockId } from '@unifyapps/defs/types/block';
import { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import { snapdomToImg } from '../../../../../utils/snapdomToImg';
import { useSharedFileProgress } from '../../../../context/SharedFileProgressProvider';
import { useGetDeviceDetailsInStateStore } from '../../../../stores/GlobalStateStore';
import { BlockDataAttributes } from '../../../useBlockDataAttributes';

/**
 * returns the full export dimensions for an element, accounting for content
 * that overflows the visible (clipped) rect via scrollWidth/scrollHeight.
 */
function getExportDimensions(el: HTMLElement): { width: number; height: number } {
  const rect = el.getBoundingClientRect();
  return {
    width: Math.max(rect.width, el.scrollWidth),
    height: Math.max(rect.height, el.scrollHeight),
  };
}

/**
 * clones the element and applies styles needed for a clean off-screen paint:
 * - explicit pixel dimensions so the capture doesn't read a clipped rect
 * - absolute + zIndex:-1 so it doesn't affect visible layout while in DOM
 * - overflow:visible and reset transform to ensure all content is captured
 */
function createExportClone(el: HTMLElement, width: number, height: number): HTMLElement {
  const clone = el.cloneNode(true) as HTMLElement;

  Object.assign(clone.style, {
    width: `${width}px`,
    height: `${height}px`,
    position: 'absolute',
    top: '0',
    left: '0',
    zIndex: '-1',
    overflow: 'visible',
    transform: 'scale(1)',
    transformOrigin: 'top left',
  });

  return clone;
}

function useExportBlock() {
  const pageEl = useContainerElementContext();
  const { setShowFileDownloadDialog } = useSharedFileProgress();
  const { device } = useGetDeviceDetailsInStateStore();
  const isMobile = device === DeviceVariantType.MOBILE;

  const onExportBlock = useEventCallback(async (blockId: BlockId) => {
    if (!blockId || !pageEl) return;

    let clone: HTMLElement | null = null;

    try {
      const elToExport = pageEl.querySelector(
        `[${BlockDataAttributes.BlockId}="${blockId}"]`,
      ) as HTMLElement | null;

      if (!elToExport) return;

      // Show loading state for mobile
      if (isMobile) {
        setShowFileDownloadDialog(true);
      }

      const { width, height } = getExportDimensions(elToExport);
      clone = createExportClone(elToExport, width, height);

      /** --- append temporarily to container (ensures full layout paint) --- */
      pageEl.appendChild(clone);

      // the clone already carries explicit pixel dimensions, so snapdom measures the full
      // box without us re-passing width/height.
      await snapdomToImg(clone, {
        download: true,
        // on mobile, match device DPR for sharp output but cap at 3x to avoid OOM on high-DPR devices; desktop uses 1x to keep file size reasonable
        dpr: isMobile ? Math.min(3, window.devicePixelRatio || 2) : 1,
      });
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      /** --- 5️⃣ Clean up cloned element and reset state --- */
      clone?.remove();

      // Reset mobile dialog state
      if (isMobile) {
        setShowFileDownloadDialog(false);
      }
    }
  });

  return { onExportBlock };
}

export default useExportBlock;
